import { blinkConstants } from '@thoughtspot/blink-constants';
import { logger } from '@thoughtspot/client-state-util/esm/utils';
import { getFormatPattern } from '@thoughtspot/formatter-service';
import { translate } from '@thoughtspot/i18n';
import { getRandomInteger } from '@thoughtspot/js-util';
import { isColumnValidForNumberFormatting } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { ABOVE_EXPECTED_COLOR_INDEX, BELOW_EXPECTED_COLOR_INDEX, } from '../../components/kpi/cortex-utils';
import { negativeComparisionColorIndex, positiveComparisionColorIndex, } from '../../components/kpi/kpi-utils';
import { getLinkedColumnForAxis, getYAxisColumns, } from '../../core/chart-model-utils';
import { chartSpecificConstants, configOptions, getLegendLabels, } from '../../core/chart-type-specification-service';
import { getXAxisLabelAt } from '../../data/data-util';
import { ChartType, } from '../../types';
export const getCandleStickChartColorItems = (customColors) => {
    const labelForUpMark = translate('chartSettingsV2.customColorConfigurator.candlestick.UP_COLOR');
    const labelForDownMark = translate('chartSettingsV2.customColorConfigurator.candlestick.DOWN_COLOR');
    const colors = _.isEmpty(customColors) || customColors.length === 0
        ? chartSpecificConstants.DEFAULT_CANDLESTICK_COLORS
        : customColors;
    return [
        { color: colors[0], serieName: labelForUpMark },
        { color: colors[1], serieName: labelForDownMark },
    ];
};
export const getKpiChartColorItems = (customColors, cortexAnomaly = false) => {
    const defaultCustomColors = chartSpecificConstants.DEFAULT_NEW_KPI_COLORS;
    const colors = _.map(defaultCustomColors, (defaultColor, index) => {
        return customColors[index] || defaultColor;
    });
    if (cortexAnomaly) {
        const labelForAboveExpectedColor = translate('ABOVE_EXPECTED_ANOMALY');
        const labelForBelowExpectedColor = translate('BELOW_EXPECTED_ANOMALY');
        return [
            {
                color: colors[ABOVE_EXPECTED_COLOR_INDEX],
                serieName: labelForAboveExpectedColor,
            },
            {
                color: colors[BELOW_EXPECTED_COLOR_INDEX],
                serieName: labelForBelowExpectedColor,
            },
        ];
    }
    const labelForPositiveColor = translate('charts.chartEditor.KPI.COLOR_LABEL_POSITIVE');
    const labelForNegativeColor = translate('charts.chartEditor.KPI.COLOR_LABEL_NEGATIVE');
    return [
        {
            color: colors[positiveComparisionColorIndex],
            serieName: labelForPositiveColor,
        },
        {
            color: colors[negativeComparisionColorIndex],
            serieName: labelForNegativeColor,
        },
    ];
};
export const getUnifiedMultiColorSeriesColors = (multiColorSeriesColors, systemMultiColorSeriesColors) => {
    if (!systemMultiColorSeriesColors)
        return _.cloneDeep(multiColorSeriesColors) || [];
    const combinedMultiColorSeriesColors = _.unionBy(_.cloneDeep(multiColorSeriesColors), _.cloneDeep(systemMultiColorSeriesColors), 'serieName');
    systemMultiColorSeriesColors.forEach(systemColor => {
        var _a;
        const existingColorMap = (_a = combinedMultiColorSeriesColors.find(colMap => colMap.serieName === systemColor.serieName)) === null || _a === void 0 ? void 0 : _a.colorMap;
        if (existingColorMap) {
            systemColor.colorMap.forEach(colorMapItem => {
                if (!existingColorMap.some(colorMap => colorMap.serieName === colorMapItem.serieName)) {
                    existingColorMap.push(colorMapItem);
                }
            });
        }
    });
    return combinedMultiColorSeriesColors;
};
export const getUnifiedColorMap = (userDefined, systemDefined) => {
    const userDefinedFiltered = userDefined.filter(serie => !_.isEmpty(serie.serieName) && !_.isEmpty(serie.color));
    const systemDefinedFiltered = systemDefined.filter(serie => !_.isEmpty(serie.serieName) && !_.isEmpty(serie.color));
    const colorMap = _.unionBy(userDefinedFiltered, systemDefinedFiltered, 'serieName');
    const lowercaseNameMap = new Map();
    return colorMap.filter(({ serieName }, index) => {
        const lowercaseName = serieName.toLowerCase();
        if (!lowercaseNameMap.has(lowercaseName)) {
            lowercaseNameMap.set(lowercaseName, true);
            if (serieName !== lowercaseName) {
                colorMap[index].serieName = lowercaseName;
            }
            return true;
        }
        return false;
    });
};
export const getDataWithColor = (dataValues, colorMap) => {
    return _.reduce(dataValues, (result, value) => {
        var _a;
        const colorItem = {
            serieName: value.label,
            color: (_a = colorMap.find(item => item.serieName === value.id)) === null || _a === void 0 ? void 0 : _a.color,
        };
        if (colorItem.color) {
            result.push(colorItem);
        }
        else {
            logger.debug(`unable to find color for the label ${colorItem.serieName}`);
        }
        return result;
    }, []);
};
export const getLegendDataValues = (chartModel, dataModel) => {
    const chartType = chartModel.config.chartType;
    const chartConfigOption = configOptions(chartType, chartModel);
    if (chartConfigOption.allowedConfigurations.configureColorForXAttrs) {
        return dataModel.xValueToRawValues.map((rawValue, idx) => {
            const label = getXAxisLabelAt(chartModel, dataModel, idx, undefined, undefined, translate);
            return {
                label,
                id: label.toLowerCase(),
            };
        });
    }
    let legendDataValues = getLegendLabels(chartModel, dataModel);
    if (chartType === ChartType.LineStackedColumn) {
        getYAxisColumns(chartModel).forEach((col, index) => {
            if (index > 0) {
                legendDataValues = legendDataValues.filter(data => col.id !== data.id);
            }
        });
    }
    return legendDataValues;
};
export const getFormatPatternForAxis = (chartModel, axisId) => {
    const linkedVizColumns = getLinkedColumnForAxis(chartModel, axisId);
    const columnToConsider = _.find(linkedVizColumns, col => isColumnValidForNumberFormatting(col));
    if (!columnToConsider) {
        return null;
    }
    return getFormatPattern(columnToConsider);
};
export const getRandomColorFromPalette = (allColors) => {
    var _a;
    const primaryColorCount = blinkConstants.colorLayout.COLOR_COUNT_DEFAULT;
    const colorVariationCount = blinkConstants.colorLayout.COLOR_VARIATION_COUNT;
    const randomInt = getRandomInteger(0, primaryColorCount - 2);
    const colorIndex = randomInt * colorVariationCount + 2;
    return (_a = allColors[colorIndex % allColors.length]) !== null && _a !== void 0 ? _a : '#2E75F0';
};
//# sourceMappingURL=common-utils.js.map